// src/pages/AdminPrintCodes.jsx
import React, { useEffect, useMemo, useState } from 'react';
import PageLayout from '../components/PageLayout';
import { CONFIG } from '../config/config';

const emptyForm = { name: '', kind: 'qr', template: '', enabled: true };

export default function AdminPrintCodes() {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const api = useMemo(
    () => ({
      listAdmin: `${CONFIG.API_URL}/print-code-templates/admin`,
      base: `${CONFIG.API_URL}/print-code-templates`,
    }),
    [],
  );

  const fetchList = async () => {
    if (!token) return;
    setLoading(true);
    setErr('');
    try {
      const r = await fetch(api.listAdmin, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setErr("Impossible de charger les templates (back KO ?).");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    setErr('');
    try {
      const isEdit = Boolean(editingId);
      const url = isEdit ? `${api.base}/${editingId}` : api.base;
      const method = isEdit ? 'PATCH' : 'POST';

      const r = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: form.name,
          kind: form.kind,
          template: form.template,
          enabled: Boolean(form.enabled),
        }),
      });

      if (!r.ok) {
        const j = await r.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${r.status}`);
      }

      setForm(emptyForm);
      setEditingId(null);
      fetchList();
    } catch (e) {
      console.error(e);
      setErr(String(e?.message || e));
    }
  };

  const onEdit = (it) => {
    setEditingId(it.id);
    setForm({
      name: it.name || '',
      kind: it.kind || 'qr',
      template: it.template || '',
      enabled: Boolean(it.enabled),
    });
  };

  const onDelete = async (id) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    setErr('');
    try {
      const r = await fetch(`${api.base}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok && r.status !== 204) throw new Error(`HTTP ${r.status}`);
      fetchList();
    } catch (e) {
      console.error(e);
      setErr('Suppression impossible.');
    }
  };

  return (
    <PageLayout title="Administration — Codes écran">
      <div className="px-4 py-4">
        <div className="rounded-2xl border bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold">Templates (QR / Code128)</h2>
            <button
              onClick={fetchList}
              className="rounded-lg border px-3 py-2 text-sm hover:bg-slate-50"
            >
              Rafraîchir
            </button>
          </div>

          {err && <div className="mt-3 text-sm text-red-600">⚠️ {err}</div>}

          <div className="mt-4 grid gap-3">
            <div className="grid gap-2 rounded-xl border bg-slate-50 p-3">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <label className="text-xs text-slate-600">Nom</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                    placeholder="ex: QR passage"
                  />
                </div>

                <div>
                  <label className="text-xs text-slate-600">Type</label>
                  <select
                    value={form.kind}
                    onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                  >
                    <option value="qr">QR 2D</option>
                    <option value="code128">Code128 1D</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-600">Template</label>
                <textarea
                  rows={3}
                  value={form.template}
                  onChange={(e) => setForm((f) => ({ ...f, template: e.target.value }))}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm font-mono"
                  placeholder="ex: {{nomAscii}}\\t{{prenomAscii}}\\t{{musiqId}}\\t{{JJ}}/{{MM}}/{{AA}}"
                />
                <div className="mt-1 text-[11px] text-slate-500">
                  Placeholders utiles :{' '}
                  <span className="font-mono">
                    {
                      '{{musiqId}} {{nom}} {{prenom}} {{civilite}} {{sexe}} {{nomAscii}} {{prenomAscii}} {{ddn}} {{ddn_jour}} {{ddn_mois}} {{ddn_annee}} {{ddn_annee_courte}} {{JJ}} {{MM}} {{AAAA}} {{AA}} {{jj}} {{mm}} {{aaaa}} {{aa}} {{todayISO}}'
                    }
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  Séquences spéciales : <span className="font-mono">\t</span> = tabulation,{' '}
                  <span className="font-mono">\n</span> = nouvelle ligne,{' '}
                  <span className="font-mono">\r</span> = retour chariot.
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  />
                  Activé
                </label>

                <div className="flex-1" />

                {editingId && (
                  <button
                    onClick={() => {
                      setEditingId(null);
                      setForm(emptyForm);
                    }}
                    className="rounded-lg border px-3 py-2 text-sm hover:bg-white"
                  >
                    Annuler
                  </button>
                )}

                <button
                  onClick={onSubmit}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  {editingId ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </div>

            <div className="rounded-xl border">
              <div className="border-b px-3 py-2 text-xs font-semibold uppercase text-slate-500">
                Liste
              </div>

              {loading ? (
                <div className="p-3 text-sm text-slate-600">Chargement…</div>
              ) : items.length === 0 ? (
                <div className="p-3 text-sm text-slate-600">Aucun template.</div>
              ) : (
                <div className="divide-y">
                  {items.map((it) => (
                    <div key={it.id} className="flex items-center gap-3 px-3 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold truncate">
                          {it.name}{' '}
                          <span className="ml-2 text-[11px] font-normal text-slate-500">
                            ({it.kind})
                          </span>
                          {!it.enabled && (
                            <span className="ml-2 text-[11px] text-slate-500">(désactivé)</span>
                          )}
                        </div>
                        <div className="mt-1 text-[12px] text-slate-600 font-mono break-all">
                          {it.template}
                        </div>
                      </div>

                      <button
                        onClick={() => onEdit(it)}
                        className="rounded-lg border px-3 py-2 text-sm hover:bg-slate-50"
                      >
                        Éditer
                      </button>
                      <button
                        onClick={() => onDelete(it.id)}
                        className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}